import { IClientPassengerFullVisa, IClientVisaBrief } from '@bustickus/models-client';
import { ApiVisas } from 'common/api';
import { handleErrorAsync } from 'common/handlers';
import { Box, Flex, Loader } from 'components';
import { useEffect, useState } from 'react';
import { AddVisaCard, VisaCard } from '..';
import { IVisaCard, IVisaInput } from '../../types';

interface IProps {
  open: boolean;
  visaId: string | null;
  onOpen: (countryId: string | null) => void;
  onChanged: (visas: (IVisaInput | null)[]) => void;
  onGetVisaClick: () => void;
  initial?: IClientPassengerFullVisa[] | null;
}

function VisaList(props: IProps) {
  const [visas, setVisas] = useState<IClientVisaBrief[] | null>(null);
  const [visaInputs, setVisaInputs] = useState<IVisaCard[]>([]);

  useEffect(() => {
    initiateVisas();
  }, []);

  const initiateVisas = async () => {
    handleErrorAsync(async () => {
      const visas = await ApiVisas.getInstance().getAll();

      if (props.initial != null) {
        const visaInputs: IVisaCard[] = [];

        for (const passengerVisa of props.initial) {
          const visaIndex = visas.findIndex((x) => x.id == passengerVisa.visaId);

          if (visaIndex < 0) {
            continue;
          }

          const visa = visas[visaIndex];

          const visaBrief: IClientVisaBrief = {
            id: visa.id,
            name: visa.name,
          };

          const visaInput: IVisaCard = {
            visa: visaBrief,
            input: {
              visa: visaBrief,
              number: passengerVisa.number,
              issueDate: new Date(passengerVisa.enterAfter),
              expirationDate: new Date(passengerVisa.enterBefore),
              stayDaysCount: passengerVisa.durationOfStay,
            },
          };

          visaInputs.push(visaInput);
          visas.splice(visaIndex, 1);
        }

        setVisaInputs(visaInputs);
      }

      setVisas(visas);
    });
  };

  const handleVisaAddClick = (visa: IClientVisaBrief) => {
    const availableVisas = visas?.filter((x) => x.id !== visa.id);
    setVisas(availableVisas ?? []);

    const updatedVisas: IVisaCard[] = [...visaInputs, { visa: visa, input: null }];
    setVisaInputs(updatedVisas);

    props.onChanged(updatedVisas.map((x) => x.input));
    props.onOpen(visa.id);
  };

  const handleDeleteVisaClick = (visa: IClientVisaBrief) => {
    setVisas([...(visas ?? []), visa]);

    const updatedVisas = visaInputs.filter((x) => x.visa.id !== visa.id);
    setVisaInputs(updatedVisas);

    props.onChanged(updatedVisas.map((x) => x.input));
    props.onOpen(null);
  };

  const handleVisaChanged = (card: IVisaCard) => {
    const visa = card.visa;
    const input = card.input;

    const visaIndex = visaInputs.findIndex((x) => x.visa.id === visa.id);

    if (visaIndex >= 0) {
      const updatedVisas = visaInputs.filter((x) => x.visa.id !== visa.id);
      updatedVisas.splice(visaIndex, 0, { visa: visa, input: input });

      setVisaInputs(updatedVisas);
      props.onChanged(updatedVisas.map((x) => x.input));
    }
  };

  if (visas == null) {
    return (
      <Box padding={20}>
        <Flex justifyContent='center'>
          <Loader
            size='default'
            variant='line'
            color='background-01'
          />
        </Flex>
      </Box>
    );
  }

  return (
    <Flex
      direction='column'
      gap={20}
    >
      {visaInputs.map((visaCard) => (
        <VisaCard
          key={visaCard.visa.id}
          open={props.open && props.visaId === visaCard.visa.id}
          onOpen={() => props.onOpen(visaCard.visa.id)}
          onDeleteClick={() => handleDeleteVisaClick(visaCard.visa)}
          visa={visaCard.visa}
          initial={visaCard}
          onChange={handleVisaChanged}
        />
      ))}

      {visas.length > 0 && (
        <AddVisaCard
          visas={visas}
          open={props.open && props.visaId == null}
          onOpen={() => props.onOpen(null)}
          onAddClick={handleVisaAddClick}
          onGetVisaClick={props.onGetVisaClick}
        />
      )}
    </Flex>
  );
}

export default VisaList;
